import { useState, useRef, useEffect } from "react";
import { useGetTodos, useCreateTodo, useUpdateTodo, useDeleteTodo, type Todo } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { CheckSquare, Trash2, Plus, Calendar, X, Pencil } from "lucide-react";
import { useSettingsFormat } from "@/hooks/use-settings-format";

function isOverdue(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [year, month, day] = dateStr.split("-").map(Number);
  const deadline = new Date(year, month - 1, day);
  return deadline < today;
}

function todayString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function TodoWidget() {
  const [inputValue, setInputValue] = useState("");
  const [newDeadline, setNewDeadline] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  const { data: todos, isLoading } = useGetTodos();
  const createTodo = useCreateTodo();
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();
  const { formatDate } = useSettingsFormat();

  const incompleteTodos = todos?.filter((t: Todo) => !t.completed) ?? [];
  const remainingCount = incompleteTodos.length;

  const handleAdd = () => {
    const label = inputValue.trim();
    if (!label) return;
    createTodo.mutate(
      { label, deadline: newDeadline || null },
      {
        onSuccess: () => {
          setInputValue("");
          setNewDeadline("");
          inputRef.current?.focus();
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAdd();
    }
  };

  useEffect(() => {
    if (editingId !== null) {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }
  }, [editingId]);

  const startEditing = (todo: Todo) => {
    setEditingId(todo.id);
    setEditingValue(todo.label);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const commitEditing = () => {
    if (editingId === null) return;
    const label = editingValue.trim();
    const id = editingId;
    setEditingId(null);
    const original = todos?.find((t: Todo) => t.id === id)?.label;
    if (!label || label === original) return;
    updateTodo.mutate({ id, label });
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEditing();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditing();
    }
  };

  return (
    <Card className="shadow-sm border-border/50 flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">To-Do</CardTitle>
        </div>
        {!isLoading && (
          <Badge variant="secondary" className="text-xs">
            {remainingCount} remaining
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 flex-1">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a task..."
            className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <div className="relative flex items-center shrink-0">
            <input
              type="date"
              value={newDeadline}
              min={todayString()}
              onChange={(e) => setNewDeadline(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background text-sm shadow-sm opacity-0 absolute inset-0 cursor-pointer"
              aria-label="Deadline date"
            />
            <div
              className={`h-10 px-3 rounded-md border border-input bg-background flex items-center gap-2 pointer-events-none text-sm ${
                newDeadline ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <Calendar className="h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">
                {newDeadline ? formatDate(newDeadline) : "Due date"}
              </span>
            </div>
          </div>
          <button
            onClick={handleAdd}
            disabled={!inputValue.trim() || createTodo.isPending}
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border border-input bg-background text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none shrink-0"
          >
            <Plus className="h-4 w-4" />
            Add task
          </button>
        </div>

        <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-16 rounded" />
              </div>
            ))
          ) : todos?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No tasks yet. Add one above!
            </p>
          ) : (
            todos?.map((todo: Todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-3 py-2.5 px-2 rounded-md hover:bg-muted/40 group transition-colors"
              >
                <input
                  type="checkbox"
                  checked={todo.completed}
                  onChange={(e) =>
                    updateTodo.mutate({ id: todo.id, completed: e.target.checked })
                  }
                  className="h-5 w-5 shrink-0 rounded border-input accent-primary cursor-pointer"
                  aria-label={`Mark "${todo.label}" as ${todo.completed ? "incomplete" : "complete"}`}
                />
                <div className="flex-1 min-w-0">
                  {editingId === todo.id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={commitEditing}
                      className="block w-full text-sm rounded border border-input bg-background px-1.5 py-0.5 -mx-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  ) : (
                    <span
                      onDoubleClick={() => startEditing(todo)}
                      className={`block text-sm truncate cursor-text ${
                        todo.completed ? "line-through text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {todo.label}
                    </span>
                  )}
                  {todo.deadline && (
                    <span
                      className={`text-xs ${
                        !todo.completed && isOverdue(todo.deadline)
                          ? "text-destructive font-medium"
                          : "text-muted-foreground"
                      }`}
                    >
                      Due {formatDate(todo.deadline)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
                  <button
                    onClick={() => startEditing(todo)}
                    className="h-8 w-8 flex items-center justify-center rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                    aria-label={`Edit "${todo.label}"`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <div className="relative flex items-center">
                    <input
                      type="date"
                      value={todo.deadline ?? ""}
                      onChange={(e) =>
                        updateTodo.mutate({ id: todo.id, deadline: e.target.value || null })
                      }
                      className="h-8 w-8 rounded opacity-0 absolute inset-0 cursor-pointer"
                      aria-label={`Set deadline for "${todo.label}"`}
                    />
                    <button className="h-8 w-8 flex items-center justify-center rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors pointer-events-none">
                      <Calendar className={`h-4 w-4 ${todo.deadline ? "text-primary" : ""}`} />
                    </button>
                  </div>
                  {todo.deadline && (
                    <button
                      onClick={() => updateTodo.mutate({ id: todo.id, deadline: null })}
                      className="h-8 w-8 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                      aria-label={`Clear deadline for "${todo.label}"`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteTodo.mutate(todo.id)}
                    className="h-8 w-8 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                    aria-label={`Delete "${todo.label}"`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
